import { createSessionRefresher } from "@dolfin/lib";
import type { components } from "@dolfin/api-admin";
import { ACTIVE_CLIENT_KEY, SESSION_STORAGE_KEY } from "@/lib/constants";

// The stored portal session and its refresher. Lives apart from AuthContext so the API client can refresh
// without importing React state.

export type UserOrganisation = components["schemas"]["AuthOrganisation"];
export type UserClient = components["schemas"]["AuthClient"];
export type AuthApiResponse = components["schemas"]["AuthResponse"];

export interface User {
  userId: string;
  email: string;
  name?: string;
  organisations: UserOrganisation[];
  clients: UserClient[];
}

/** Set when the session came from local dev login rather than Google. */
export interface DevPersona {
  key: string;
  label: string;
}

export interface Session {
  token: string;
  user: User;
  activeClientId: string;
  devPersona?: DevPersona | null;
}

export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const base64 = token.split(".")[1];
    if (!base64) return null;
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

export function userFromAuthResponse(data: AuthApiResponse): User {
  const payload = decodeJwtPayload(data.token);
  return {
    userId: data.userId,
    email: data.email,
    name: payload?.name as string | undefined,
    organisations: data.organisations ?? [],
    clients: data.clients ?? [],
  };
}

export function readStoredSession(): Session | null {
  try {
    const stored = localStorage.getItem(SESSION_STORAGE_KEY);
    return stored ? (JSON.parse(stored) as Session) : null;
  } catch {
    return null;
  }
}

// A refresh re-reads memberships: keep the active client while the user still belongs to it. Losing every
// client membership leaves nothing the portal can show, so that ends the session.
function applyRefresh(stored: Session | null, response: AuthApiResponse): Session | null {
  const user = userFromAuthResponse(response);
  if (user.clients.length === 0) return null;
  const remembered = stored?.activeClientId ?? localStorage.getItem(ACTIVE_CLIENT_KEY);
  const activeClientId = user.clients.some((c) => c.clientId === remembered) ? remembered! : user.clients[0]!.clientId;
  return { token: response.token, user, activeClientId, devPersona: stored?.devPersona ?? null };
}

export const sessionRefresher = createSessionRefresher<Session, AuthApiResponse>({
  storageKey: SESSION_STORAGE_KEY,
  apply: applyRefresh,
});
